import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bold, Italic, Underline, Strikethrough, 
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Indent, Outdent,
  Type, Palette, Highlighter, Link, Image as ImageIcon,
  Table, Quote, Heading1, Heading2, Heading3,
  Undo2, Redo2, Eraser, FileText, Download,
  Printer, Search, Save, Trash2,
  X, ChevronDown, ChevronUp,
  Hash, Languages,
  Plus, Minus, Hash as HashIcon,
  ArrowLeft, Pilcrow,
  MoveVertical, 
  BetweenVerticalEnd, BetweenVerticalStart,
  Subscript, Superscript, CheckSquare, Code2,
  ZoomIn, ZoomOut, RotateCcw, PanelTopClose, PanelTop,
  Loader2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import html2pdf from 'html2pdf.js';
import { db, auth, storage } from '../../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import renderMathInElement from 'katex/contrib/auto-render';

// Convert raw $...$ / $$...$$ / \(...\) / \[...\] LaTeX to rendered KaTeX HTML
const renderMathInHtml = (html: string): string => {
  // Block math: $$...$$ or \[...\]
  let result = html.replace(/\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g, (_m, g1, g2) => {
    try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
    catch { return _m; }
  });
  // Inline math: $...$ (not $$) or \(...\)
  result = result.replace(/(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)|\\\(([\s\S]+?)\\\)/g, (_m, g1, g2) => {
    try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
    catch { return _m; }
  });
  return result;
};

// ─── Tiptap Core ───
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Underline as UnderlineExt } from '@tiptap/extension-underline';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Link as LinkExt } from '@tiptap/extension-link';
import { Table as TableExt } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Placeholder } from '@tiptap/extension-placeholder';
import { FontFamily } from '@tiptap/extension-font-family';
import { Subscript as SubscriptExt } from '@tiptap/extension-subscript';
import { Superscript as SuperscriptExt } from '@tiptap/extension-superscript';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';

// ─── Custom Extensions ───
import { FontSize, LineHeight, Indent as IndentExt } from './editor/customExtensions';
import { ResizableImage } from './editor/ResizableImageView';
import SubTopicContentViewer from './SubTopicContentViewer';
import { BookOpen } from 'lucide-react';

// ═══════════════════════════════════════════
// LOCATION-STATE PERSISTENCE
// Persist the writer's context (subjectId, subjectName, docType, etc.) so a
// page refresh — which clears React Router's location.state — still lands
// the user on the same course outline they were working on.
// ═══════════════════════════════════════════
const OUTLINE_WRITER_STATE_KEY = 'vochak_outline_writer_state';

const readPersistedWriterState = (): Record<string, any> | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(OUTLINE_WRITER_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const persistableWriterStateFields = [
  'subjectId',
  'subjectName',
  'docType',
  'topicId',
  'subTopicId',
  'subTopicTitle',
  'activeItemId',
  'isFirebaseSubject',
  'returnPath'
] as const;

// ═══════════════════════════════════════════
// CONTENT ITEM MODEL — supports multiple PDFs and outlines per sub-topic
// ═══════════════════════════════════════════
interface ContentItem {
  id: string;
  type: 'pdf' | 'outline';
  title: string;
  url?: string;        // PDFs only
  content?: string;     // Outlines only (HTML)
  createdAt?: any;
  updatedAt?: any;
}

const newItemId = () => `it_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// Migrate legacy single-content doc to items array
const migrateLegacyToItems = (data: any): ContentItem[] => {
  const items: ContentItem[] = [];
  if (data.outlineContent || data.outlineDocName) {
    items.push({
      id: newItemId(),
      type: 'outline',
      title: data.outlineDocName || 'Main Outline',
      content: data.outlineContent || '',
      createdAt: data.lastUpdated || null,
      updatedAt: data.lastUpdated || null,
    });
  }
  if (data.referencePdfUrl) {
    items.push({
      id: newItemId(),
      type: 'pdf',
      title: 'Reference PDF',
      url: data.referencePdfUrl,
      createdAt: data.lastUpdated || null,
      updatedAt: data.lastUpdated || null,
    });
  }
  return items;
};

// ═══════════════════════════════════════════
// TOOLBAR BUTTON
// ═══════════════════════════════════════════
interface ToolbarButtonProps {
  icon: React.ElementType;
  onClick: () => void;
  tooltip: string;
  disabled?: boolean;
  active?: boolean;
}

const ToolbarButton = ({ icon: Icon, onClick, tooltip, disabled = false, active = false }: ToolbarButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button 
        variant="ghost" size="icon" 
        onMouseDown={(e) => { e.preventDefault(); onClick(); }} 
        disabled={disabled} 
        className={cn(
          "h-8 w-8 rounded-lg transition-all",
          active ? "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/30" : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400"
        )}
      >
        {React.createElement(Icon, { className: "h-4 w-4" })}
      </Button>
    </TooltipTrigger>
    <TooltipContent side="bottom" className="text-[10px] font-bold">{tooltip}</TooltipContent>
  </Tooltip>
);

// ═══════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════
const CourseOutlineWriter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Pull context from location.state, but fall back to the last persisted
  // writer state on a hard refresh (location.state is null after refresh).
  const initialState = location.state || readPersistedWriterState() || null;
  const {
    subjectId,
    subjectName,
    docType = 'outline', // 'outline' | 'subtopic'
    topicId,
    subTopicId,
    subTopicTitle,
    isFirebaseSubject,
    returnPath
  } = initialState || { subjectId: null, subjectName: "General", isFirebaseSubject: false };

  // Persist writer context whenever location.state is explicitly set, so a
  // refresh restores the same course outline. Skips the default "General"
  // placeholder so we never pin a blank session.
  React.useEffect(() => {
    if (!location.state) return;
    const incoming = location.state as Record<string, any>;
    if (!incoming.subjectId) return;
    try {
      const persistable: Record<string, any> = {};
      persistableWriterStateFields.forEach((key) => {
        if (incoming[key] !== undefined) persistable[key] = incoming[key];
      });
      localStorage.setItem(OUTLINE_WRITER_STATE_KEY, JSON.stringify(persistable));
    } catch (e) {
      console.error('Failed to persist writer state', e);
    }
  }, [location.state]);
  
  const expectedDocName = docType === 'subtopic' ? subTopicTitle : `${subjectName} - Course Outline`;
  const storageKey = docType === 'subtopic' ? `content-${subjectId}-${topicId}-${subTopicId}` : `course-outline-${subjectId || 'draft'}`;
  
  const defaultContent = '';

  // Load saved content ONCE (no state calls during render)
  const savedDataRef = React.useRef<{ name: string; content: string } | null>(null);
  if (savedDataRef.current === null) {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const { name, data } = JSON.parse(saved);
        savedDataRef.current = { name: name || expectedDocName, content: data || defaultContent };
      } catch {
        savedDataRef.current = { name: expectedDocName, content: defaultContent };
      }
    } else {
      savedDataRef.current = { name: expectedDocName, content: defaultContent };
    }
  }

  const [docName, setDocName] = useState(savedDataRef.current.name);
  const [isSaved, setIsSaved] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [isFindReplaceOpen, setIsFindReplaceOpen] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [currentFontFamily, setCurrentFontFamily] = useState("Arial");
  const [currentFontSize, setCurrentFontSize] = useState("16");
  const [zoom, setZoom] = useState(1);
  const [isToolbarVisible, setIsToolbarVisible] = useState(true);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string | null>(null);
  const [uploadedPdfFile, setUploadedPdfFile] = useState<File | null>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const pdfInputRef = React.useRef<HTMLInputElement>(null);

  // Multiple content items per sub-topic (PDFs + outlines)
  const [items, setItems] = useState<ContentItem[]>([]);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const initialActiveItemIdRef = React.useRef<string | null>(
    (location.state as any)?.activeItemId || null
  );

  const handlePdfUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
       setUploadedPdfFile(file);
       setUploadedPdfUrl(URL.createObjectURL(file));
    } else if (file) {
       toast.error("Please upload a valid PDF file");
    }
  };

  const savePdfOnly = async () => {
    if (!uploadedPdfFile || !subjectId || !auth.currentUser) return;
    setIsUploadingPdf(true);
    const toastId = toast.loading("Saving PDF to structural registry...");
    try {
      const uid = auth.currentUser.uid;
      const rId = docType === 'subtopic' ? `content_${topicId}_${subTopicId}` : 'main';
      const fileRef = storageRef(storage, `teaching_workspaces/${uid}/courses/${subjectId}/pdfs/${rId}_${Date.now()}.pdf`);
      await uploadBytes(fileRef, uploadedPdfFile);
      const url = await getDownloadURL(fileRef);

      const dbRef = doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`, rId);

      // Register the new PDF in the items array — either as a brand-new item
      // (when no active item exists) or as an update to the active PDF slot.
      const newItem: ContentItem = {
        id: activeItemId ?? newItemId(),
        type: 'pdf',
        title: uploadedPdfFile.name.replace(/\.pdf$/i, ''),
        url,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      const updatedItems: ContentItem[] = activeItemId
        ? items.map(i => i.id === activeItemId ? newItem : i)
        : [...items, newItem];
      setItems(updatedItems);
      setActiveItemId(newItem.id);
      setUploadedPdfUrl(url);

      await setDoc(dbRef, {
        items: updatedItems,
        referencePdfUrl: url,
        lastUpdated: serverTimestamp()
      }, { merge: true });

      // bump course doc to trigger asset count recalculation
      const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);
      await updateDoc(courseRef, { resourcesUpdatedAt: serverTimestamp() });

      toast.success("PDF securely saved!", { id: toastId });
      setUploadedPdfFile(null); // Remove 'Save File' trigger since it's now cloud-bound
    } catch (err) {
      console.error(err);
      toast.error("Failed to save PDF.", { id: toastId });
    } finally {
      setIsUploadingPdf(false);
    }
  };

  const zoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2));
  const zoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));
  const zoomReset = () => setZoom(1);

  // ─── Editor Instance ───
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
        codeBlock: { HTMLAttributes: { class: 'code-block' } },
      } as any),
      UnderlineExt,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      LineHeight,
      IndentExt,
      Highlight.configure({ multicolor: true }),
      LinkExt.configure({ openOnClick: false, HTMLAttributes: { class: 'editor-link' } }),
      ResizableImage,
      TableExt.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      SubscriptExt,
      SuperscriptExt,
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder: 'Start drafting your course outline & strategy...' }),
    ],
    content: savedDataRef.current.content,
    onUpdate: () => {
      setIsSaved(false);
      // Render any $...$ / $$...$$ / \(...\) / \[...\] LaTeX in the editor
      // after Tiptap has finished updating the DOM. Wrapped in rAF so the
      // browser commits the DOM mutation before KaTeX scans it.
      requestAnimationFrame(() => {
        if (editor) renderMathInEditor(editor);
      });
    },
    editorProps: {
      attributes: {
        class: 'outline-none',
        style: 'min-height: 11in; line-height: 1.6; font-size: 16px; word-break: break-word; color: #1a1a1a;',
      },
      handlePaste: (_view, event) => {
        // Allow paste - Tiptap handles this natively with proper formatting
        return false;
      },
      handleDrop: (_view, event) => {
        // Handle image drops
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          const file = files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            const reader = new FileReader();
            reader.onload = (e) => {
              const src = e.target?.result as string;
              editor?.chain().focus().setImage({ src }).run();
            };
            reader.readAsDataURL(file);
            return true;
          }
        }
        return false;
      },
    },
  });

  // Render $...$ / $$...$$ / \(...\) / \[...\] LaTeX inside the editor's
  // contenteditable using KaTeX's auto-render. Tiptap stores the original
  // LaTeX as plain text in its document model, so getHTML() / saveDocument
  // still serialize the raw source — KaTeX only touches the DOM for display.
  const renderMathInEditor = (ed: NonNullable<typeof editor>) => {
    try {
      const dom = ed.view.dom as HTMLElement;
      // Skip the text node that currently holds the caret so typing inside
      // an expression doesn't yank the cursor away.
      const sel = window.getSelection();
      const caretNode = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).startContainer : null;
      const caretParentText = caretNode && caretNode.nodeType === Node.TEXT_NODE
        ? (caretNode as Text)
        : null;
      renderMathInElement(dom, {
        delimiters: [
          { left: '$$', right: '$$', display: true },
          { left: '$', right: '$', display: false },
          { left: '\\(', right: '\\)', display: false },
          { left: '\\[', right: '\\]', display: true },
        ],
        throwOnError: false,
        ignoredClasses: ['ProseMirror', 'ProseMirror-focused'],
        errorCallback: (_msg: string, _err: Error) => { /* swallow */ },
      });
      // If we displaced the caret's text node while rendering math, put the
      // caret back at the end of the previous sibling (best-effort).
      if (caretParentText && !document.contains(caretParentText)) {
        const range = document.createRange();
        if (dom.contains(caretParentText.parentNode)) {
          range.selectNodeContents(caretParentText.parentNode!);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      }
    } catch {
      /* never let math rendering crash the editor */
    }
  };

  // Render math once the editor mounts (covers initial content + Firebase load)
  useEffect(() => {
    if (!editor) return;
    const id = requestAnimationFrame(() => renderMathInEditor(editor));
    return () => cancelAnimationFrame(id);
  }, [editor]);

  // Fetch Firestore Data Only Once
  useEffect(() => {
    if (!editor || !subjectId || !auth.currentUser || isInitialLoadDone) return;
    const uid = auth.currentUser.uid;
    const refId = docType === 'subtopic' ? `content_${topicId}_${subTopicId}` : 'main';
    const ref = doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`, refId);

    getDoc(ref).then(snap => {
      const data = snap.data();
      if (snap.exists() && data) {
        // Load items array (with migration from legacy single-content fields)
        let loadedItems: ContentItem[] = [];
        if (Array.isArray(data.items) && data.items.length > 0) {
          loadedItems = data.items as ContentItem[];
        } else {
          loadedItems = migrateLegacyToItems(data);
        }
        setItems(loadedItems);

        // Pick the active item — prefer one passed via navigation state, then
        // the first outline (so the editor has text), then any first item.
        const preferredId = initialActiveItemIdRef.current;
        const fallback = loadedItems.find(i => i.type === 'outline') ?? loadedItems[0];
        const active = preferredId
          ? (loadedItems.find(i => i.id === preferredId) ?? fallback)
          : fallback;
        if (active) {
          setActiveItemId(active.id);
          if (active.type === 'pdf' && active.url) {
            setUploadedPdfUrl(active.url);
          } else if (active.type === 'outline') {
            editor.commands.setContent(active.content || '');
            setDocName(active.title || expectedDocName);
          }
        } else {
          setDocName(expectedDocName);
        }
      } else {
        setDocName(expectedDocName);
      }
      setIsInitialLoadDone(true);
    }).catch(err => {
      console.error("Fetch DB error:", err);
      setIsInitialLoadDone(true);
    });
  }, [editor, subjectId, isInitialLoadDone, expectedDocName, docType, topicId, subTopicId]);

  // ─── Auto-Save (30s) ───
  useEffect(() => {
    const timer = setInterval(() => {
      if (!isSaved && editor) saveDocument();
    }, 30000);
    return () => clearInterval(timer);
  }, [isSaved, editor]);

  // ─── Ctrl+S ───
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveDocument();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editor, docName]);

  // Switch the editor to a different item from the sidebar
  const switchToItem = (id: string) => {
    if (!editor) return;
    const target = items.find(i => i.id === id);
    if (!target) return;

    // Persist the currently active item's editor content into the items array
    if (activeItemId) {
      const html = editor.getHTML();
      setItems(prev => prev.map(i => i.id === activeItemId
        ? { ...i, type: 'outline', content: html, title: i.type === 'outline' ? docName : i.title, updatedAt: Date.now() }
        : i
      ));
    }

    setActiveItemId(id);
    if (target.type === 'pdf' && target.url) {
      setUploadedPdfFile(null);
      setUploadedPdfUrl(target.url);
      editor.commands.setContent('');
    } else {
      setUploadedPdfUrl(null);
      editor.commands.setContent(target.content || '');
      setDocName(target.title || expectedDocName);
    }
    setIsSaved(true);
  };

  // Add a brand new outline item
  const addOutlineItem = () => {
    if (!editor) return;
    // Persist current before switching
    if (activeItemId) {
      const html = editor.getHTML();
      setItems(prev => prev.map(i => i.id === activeItemId
        ? { ...i, content: html, updatedAt: Date.now() }
        : i
      ));
    }
    const id = newItemId();
    const newItem: ContentItem = {
      id,
      type: 'outline',
      title: `Outline ${items.filter(i => i.type === 'outline').length + 1}`,
      content: '',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setItems(prev => [...prev, newItem]);
    setActiveItemId(id);
    editor.commands.setContent('');
    setDocName(newItem.title);
    setIsSaved(false);
  };

  // Add a brand new PDF item (just registers the slot — upload via the existing PDF flow)
  const addPdfItem = () => {
    const id = newItemId();
    const newItem: ContentItem = {
      id,
      type: 'pdf',
      title: `Reference PDF ${items.filter(i => i.type === 'pdf').length + 1}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    setItems(prev => [...prev, newItem]);
    setActiveItemId(id);
    setUploadedPdfUrl(null);
    setUploadedPdfFile(null);
    if (editor) editor.commands.setContent('');
    setIsSaved(false);
    // Open the file picker immediately so the user can upload
    setTimeout(() => pdfInputRef.current?.click(), 0);
  };

  // Delete an item by id
  const deleteItem = (id: string) => {
    if (!window.confirm('Delete this content item? This cannot be undone.')) return;
    const remaining = items.filter(i => i.id !== id);
    setItems(remaining);
    if (activeItemId === id) {
      const next = remaining[0] || null;
      if (next) {
        switchToItem(next.id);
      } else {
        setActiveItemId(null);
        setUploadedPdfUrl(null);
        if (editor) editor.commands.setContent('');
      }
    }
    setIsSaved(false);
  };

  // Save the active item's editor content into the items array
  const persistActiveItem = React.useCallback(() => {
    if (!editor || !activeItemId) return null;
    const html = editor.getHTML();
    return { id: activeItemId, content: html, title: docName };
  }, [editor, activeItemId, docName]);

  const saveDocument = useCallback(async () => {
    if (!editor) {
      toast.error("Editor not ready. Please wait a moment and try again.");
      return;
    }
    setIsSaving(true);
    const html = editor.getHTML();

    // Detect effectively-empty content (just empty HTML tags, no visible text)
    const textOnly = html
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/&[a-z0-9#]+;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const hasNonTextContent = /<(img|iframe|video|audio|table|ul|ol|li|blockquote|pre|code|embed|object|svg|hr)\b/i.test(html);

    if (!textOnly && !hasNonTextContent) {
      const proceed = window.confirm("This document is empty. Save anyway?");
      if (!proceed) {
        setIsSaving(false);
        return;
      }
    }

    localStorage.setItem(storageKey, JSON.stringify({
      name: docName,
      data: html,
      lastSaved: new Date().toISOString()
    }));

    if (subjectId && auth.currentUser) {
      try {
        const uid = auth.currentUser.uid;
        const refId = docType === 'subtopic' ? `content_${topicId}_${subTopicId}` : 'main';
        const ref = doc(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`, refId);

        // Build the items array: update active item, keep others intact
        const updatedItems: ContentItem[] = activeItemId
          ? items.map(i => i.id === activeItemId
            ? { ...i, type: 'outline', content: html, title: docName, updatedAt: Date.now() }
            : i
          )
          : items;

        await setDoc(ref, {
          items: updatedItems,
          outlineDocName: docName,
          outlineContent: html,
          lastUpdated: serverTimestamp()
        }, { merge: true });

        // bump course doc so assets listener updates
        const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);
        await updateDoc(courseRef, { resourcesUpdatedAt: serverTimestamp() });

        setIsSaved(true);
        setIsSaving(false);
        toast.success("Outline saved securely");
      } catch (err) {
        console.error("DB Save Error:", err);
        setIsSaving(false);
        toast.error("Failed to save outline. Please try again.");
        return;
      }
    } else {
      setIsSaved(true);
      setIsSaving(false);
      toast.success("Outline saved (local only — no course linked)");
    }
  }, [editor, docName, subjectId, storageKey, docType, topicId, subTopicId, activeItemId, items]);

  const clearContent = () => {
    if (window.confirm("Clear the entire page? This cannot be undone.")) {
      editor?.commands.clearContent();
      setIsSaved(false);
      toast.info("Document cleared");
    }
  };

  // ─── Exports ───
  const exportAsPDF = () => {
    if (!editor) return;
    const htmlToPrint = renderMathInHtml(editor.getHTML());
    const popup = window.open('', '', 'height=900,width=800');
    if (!popup) { alert('Please allow popups to export as PDF'); return; }
    const katexCss = '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css">';
    const styles = `<style>
        body { font-family: 'Times New Roman', Times, serif; font-size: 11pt; line-height: 1.4; color: #333; margin: 0; padding: 20px; }
        @media print { body { padding: 0; } }
      </style>`;

    popup.document.write(`<html><head><title>${docName}</title>${katexCss}${styles}</head><body>${htmlToPrint}</body></html>`);
    popup.document.close();
    
    setTimeout(() => {
      popup.print();
    }, 1000);
  };

  const exportAsDOC = () => {
    if (!editor) return;
    const renderedHtml = renderMathInHtml(editor.getHTML());
    const header = "<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'></head><body>";
    const a = document.createElement("a");
    a.href = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(header + renderedHtml + "</body></html>");
    a.download = `${docName}.doc`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const exportAsHTML = () => {
    if (!editor) return;
    const renderedHtml = renderMathInHtml(editor.getHTML());
    const fullHtml = `<html><head><title>${docName}</title><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.8/dist/katex.min.css"></head><body>${renderedHtml}</body></html>`;
    const blob = new Blob([fullHtml], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `${docName}.html`; a.click();
  };

  const exportAsTXT = () => {
    if (!editor) return;
    const blob = new Blob([editor.getText()], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `${docName}.txt`; a.click();
  };

  // ─── Insert Operations ───
  const insertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (re) => {
        editor?.chain().focus().setImage({ src: re.target?.result as string }).run();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const insertTable = () => {
    const rows = prompt("Rows:", "3");
    const cols = prompt("Columns:", "3");
    if (rows && cols) {
      editor?.chain().focus().insertTable({ rows: parseInt(rows), cols: parseInt(cols), withHeaderRow: true }).run();
    }
  };

  const insertLink = () => {
    if (editor?.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    const url = prompt('Enter URL:');
    if (url) editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const findAndReplace = () => {
    if (!editor || !findText) return;
    const html = editor.getHTML();
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    editor.commands.setContent(html.replace(regex, replaceText));
    toast.success("Replaced all instances");
  };

  // ─── Stats ───
  const wordCount = editor ? (editor.getText().trim() ? editor.getText().trim().split(/\s+/).length : 0) : 0;
  const charCount = editor ? editor.getText().length : 0;

  const fontFamilies = [
    "Arial", "Helvetica", "Georgia", "Times New Roman", "Courier New", "Verdana", "Impact", "Comic Sans MS",
    "Trebuchet MS", "Arial Black", "Palatino", "Garamond", "Bookman", "Avant Garde",
    "Tahoma", "Geneva", "Lucida Sans Unicode", "Lucida Console", "Monaco", "Optima",
    "Brush Script MT", "Consolas", "Didot", "American Typewriter", "Andale Mono", "Cambria",
    "Candara", "Franklin Gothic Medium", "Futura", "Rockwell", "Segoe UI", "Calibri"
  ].sort();
  const fontSizes = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 30, 36, 48, 60, 72];

  if (!editor) return null;

  return (
    <TooltipProvider>
      <div className="w-full bg-slate-50 dark:bg-[#020617] flex flex-col font-sans" style={{ height: '100vh' }}>
        
        {/* ═══ HEADER ═══ */}
        <header className="min-h-16 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 lg:px-8 py-2 sm:py-0 z-40 sticky top-0 shadow-sm shrink-0">
          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 min-w-0 flex-1">
            <Button variant="ghost" size="icon" onClick={() => {
              const canGoBack = typeof window !== 'undefined' && typeof window.history.state?.idx === 'number' && window.history.state.idx > 0;
              if (canGoBack) {
                navigate(-1);
                return;
              }
              if (returnPath) {
                navigate(returnPath, { replace: true, state: { subjectId, subjectName, isFirebaseSubject } });
              } else {
                docType === 'subtopic'
                  ? navigate('/dashboard/lessons/course/structure', { replace: true, state: { subjectId, subjectName, isFirebaseSubject } })
                  : navigate('/dashboard/lessons', { replace: true });
              }
            }} className="rounded-full h-9 w-9 sm:h-10 sm:w-10 border border-slate-200 dark:border-slate-800 shrink-0">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex flex-col min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5 min-w-0">
                <span className="px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/40 text-[9px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest shrink-0">Stage 4</span>
                <input 
                  value={docName} 
                  onChange={(e) => { setDocName(e.target.value); setIsSaved(false); }} 
                  className="bg-transparent border-none p-0 text-sm font-bold focus:ring-0 text-slate-800 dark:text-white w-full min-w-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded px-1 transition-colors" 
                  placeholder="Rename document..."
                />
              </div>
              <div className="flex items-center gap-2 sm:gap-3 text-[10px] text-muted-foreground font-medium uppercase tracking-tighter">
                <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> <span className="hidden sm:inline">Outline Writing</span></span>
                <div className="h-1 w-1 rounded-full bg-slate-300" />
                <span className={isSaved ? 'text-emerald-500' : 'text-amber-500 font-bold'}>{isSaved ? 'Synced' : 'Drafting...'}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
             {!uploadedPdfUrl && (
               <div className="hidden lg:flex items-center gap-1 mr-4 bg-slate-50 dark:bg-slate-800/50 p-1 rounded-lg border border-slate-100 dark:border-slate-700/50">
                 <span className="text-[10px] font-bold px-2 text-slate-400 text-xs">STATS:</span>
                 <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-1" />
                 <span className="text-[10px] font-bold px-2 bg-white dark:bg-slate-900 rounded-md shadow-sm text-xs">{wordCount} W</span>
                 <span className="text-[10px] font-bold px-2 text-xs">{charCount} C</span>
               </div>
             )}

             {/* ═══ ZOOM CONTROLS ═══ */}
             {!uploadedPdfUrl && (
               <div className="hidden sm:flex items-center gap-1 bg-slate-50 dark:bg-slate-800/50 p-1 rounded-lg border border-slate-100 dark:border-slate-700/50 mr-2">
                 <button onClick={zoomOut} disabled={zoom <= 0.5} className="p-1 rounded hover:bg-white dark:hover:bg-slate-700 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white disabled:opacity-30 transition-colors" title="Zoom Out"><ZoomOut className="h-4 w-4" /></button>
                 <button onClick={zoomReset} className="px-2 py-0.5 rounded text-[10px] font-bold text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700 transition-colors min-w-[42px] text-center" title="Reset Zoom">{Math.round(zoom * 100)}%</button>
                 <button onClick={zoomIn} disabled={zoom >= 2} className="p-1 rounded hover:bg-white dark:hover:bg-slate-700 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white disabled:opacity-30 transition-colors" title="Zoom In"><ZoomIn className="h-4 w-4" /></button>
               </div>
             )}

             {uploadedPdfUrl && uploadedPdfFile && (
               <Button size="sm" onClick={savePdfOnly} disabled={isUploadingPdf} className="h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-3 sm:px-4 text-xs shadow-md hover:scale-[1.02] active:scale-95 transition-all mr-2">
                 {isUploadingPdf ? 'Saving...' : <><Download className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Save File</span></>}
               </Button>
             )}

             <input type="file" ref={pdfInputRef} accept=".pdf" onChange={handlePdfUpload} className="hidden" />
             <Button variant={uploadedPdfUrl ? "secondary" : "ghost"} size="icon" onClick={() => { if (uploadedPdfUrl) { setUploadedPdfUrl(null); setUploadedPdfFile(null); } else { pdfInputRef.current?.click(); } }} className="mr-1 h-9 w-9 rounded-xl border border-slate-200 dark:border-slate-800 text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title={uploadedPdfUrl ? "Close PDF Reader" : "Upload Reference PDF"}><BookOpen className="h-4 w-4" /></Button>
             {!uploadedPdfUrl && <Button variant="ghost" size="icon" onClick={() => setIsFindReplaceOpen(true)} className="mr-2 sm:mr-4 h-9 w-9 rounded-xl border border-slate-200 dark:border-slate-800"><Search className="h-4 w-4" /></Button>}
             
             {!uploadedPdfUrl && (
               <DropdownMenu>
                 <DropdownMenuTrigger asChild>
                   <Button variant="outline" size="sm" className="h-9 sm:h-10 rounded-xl font-bold border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm px-3"><Download className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Export</span></Button>
                 </DropdownMenuTrigger>
                 <DropdownMenuContent align="end" className="w-56 rounded-2xl p-2 shadow-2xl border-slate-200 dark:border-slate-800">
                   <DropdownMenuItem onClick={exportAsDOC} className="rounded-xl p-3 gap-3 cursor-pointer group">
                     <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 flex items-center justify-center group-hover:scale-110 transition-transform"><FileText className="h-4 w-4" /></div>
                     <div className="flex flex-col"><span className="font-bold text-sm">MS Word (.doc)</span><span className="text-[10px] text-muted-foreground uppercase">Native Word Format</span></div>
                   </DropdownMenuItem>
                   <DropdownMenuItem onClick={exportAsPDF} className="rounded-xl p-3 gap-3 cursor-pointer group">
                     <div className="h-8 w-8 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-600 flex items-center justify-center group-hover:scale-110 transition-transform"><FileText className="h-4 w-4" /></div>
                     <div className="flex flex-col"><span className="font-bold text-sm">PDF Document</span><span className="text-[10px] text-muted-foreground uppercase">Best for Printing</span></div>
                   </DropdownMenuItem>
                   <DropdownMenuItem onClick={exportAsHTML} className="rounded-xl p-3 gap-3 cursor-pointer group">
                     <div className="h-8 w-8 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform"><Languages className="h-4 w-4" /></div>
                     <div className="flex flex-col"><span className="font-bold text-sm">HTML Code</span><span className="text-[10px] text-muted-foreground uppercase">Web Format</span></div>
                   </DropdownMenuItem>
                   <DropdownMenuItem onClick={exportAsTXT} className="rounded-xl p-3 gap-3 cursor-pointer group">
                     <div className="h-8 w-8 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-600 flex items-center justify-center group-hover:scale-110 transition-transform"><HashIcon className="h-4 w-4" /></div>
                     <div className="flex flex-col"><span className="font-bold text-sm">Plain Text</span><span className="text-[10px] text-muted-foreground uppercase">No Formatting</span></div>
                   </DropdownMenuItem>
                 </DropdownMenuContent>
               </DropdownMenu>
             )}
              <Button
                type="button"
                onClick={async () => { await saveDocument(); }}
                disabled={isSaving}
                className="h-8 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-3 sm:px-4 text-xs shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span className="hidden sm:inline">Saving…</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5">
                    <Save className="h-3.5 w-3.5" />
                    <span>Save</span>
                  </span>
                )}
              </Button>
          </div>
        </header>

        {/* ═══ CONTENT ITEMS BAR — manage multiple PDFs + outlines per sub-topic ═══ */}
        <div className="bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 shrink-0 z-30">
          <div className="flex items-center gap-2 px-3 sm:px-6 lg:px-8 py-2 overflow-x-auto no-scrollbar">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 shrink-0 hidden sm:inline">Content</span>
            <div className="h-4 w-px bg-slate-200 dark:bg-slate-800 shrink-0 hidden sm:block" />
            {items.length === 0 ? (
              <span className="text-[10px] text-slate-400 italic shrink-0">No content yet — add a PDF or outline below</span>
            ) : (
              items.map(item => (
                <button
                  key={item.id}
                  onClick={() => switchToItem(item.id)}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border transition-all shrink-0 group",
                    activeItemId === item.id
                      ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20"
                      : "bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-800 hover:border-indigo-300 hover:text-indigo-600"
                  )}
                  title={item.type === 'pdf' ? `PDF: ${item.title}` : `Outline: ${item.title}`}
                >
                  {item.type === 'pdf' ? (
                    <FileText className="h-3 w-3 shrink-0" />
                  ) : (
                    <BookOpen className="h-3 w-3 shrink-0" />
                  )}
                  <span className="max-w-[120px] truncate">{item.title}</span>
                  <span
                    role="button"
                    onClick={(e) => { e.stopPropagation(); deleteItem(item.id); }}
                    className={cn(
                      "ml-1 p-0.5 rounded transition-colors shrink-0",
                      activeItemId === item.id
                        ? "hover:bg-white/20"
                        : "hover:bg-rose-100 hover:text-rose-600 opacity-0 group-hover:opacity-100"
                    )}
                    title="Delete this item"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </button>
              ))
            )}
            <div className="h-4 w-px bg-slate-200 dark:bg-slate-800 shrink-0" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addOutlineItem}
              className="h-7 px-2 text-[10px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 shrink-0"
            >
              <Plus className="h-3 w-3 mr-1" /> Outline
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={addPdfItem}
              className="h-7 px-2 text-[10px] font-black uppercase tracking-widest text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 shrink-0"
            >
              <Plus className="h-3 w-3 mr-1" /> PDF
            </Button>
          </div>
        </div>

        {/* ═══ TOOLBAR TOGGLE & FLOATING BAR ═══ */}
        {!isToolbarVisible && !uploadedPdfUrl && (
          <div className="sticky top-16 z-30 flex justify-center mt-2 w-full pointer-events-none">
            <button
              onClick={() => setIsToolbarVisible(true)}
              className="pointer-events-auto flex items-center gap-1.5 px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest bg-white dark:bg-slate-800 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 rounded-full shadow-md border border-slate-200 dark:border-slate-700 transition-all hover:scale-105"
            >
              <PanelTop className="h-3.5 w-3.5" /> Show Toolbar
            </button>
          </div>
        )}

        {/* ═══ EXPANDABLE TOOLBAR ═══ */}
        {!uploadedPdfUrl && (
          <div className={`bg-white/90 dark:bg-slate-950/90 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 sticky top-16 z-20 shrink-0 transition-all duration-300 ease-in-out ${isToolbarVisible ? 'max-h-[500px] opacity-100 transform-none relative' : 'max-h-0 opacity-0 -translate-y-4 absolute w-full'}`}>
            <div className={`p-2 flex flex-wrap sm:flex-nowrap items-center justify-start sm:justify-center gap-1 overflow-x-auto no-scrollbar ${isToolbarVisible ? 'py-2' : 'py-0 overflow-hidden'}`}>
            <div className="flex items-center gap-1 border-r border-slate-200 dark:border-slate-800 pr-2 mr-2">
              <button onClick={() => setIsToolbarVisible(false)} title="Hide Toolbar" className="p-1.5 rounded-md bg-orange-500 text-white shadow-md shadow-orange-500/40 hover:bg-orange-600 hover:shadow-orange-500/60 transition-all ml-1 mr-2"><PanelTopClose className="h-4 w-4" /></button>
              <ToolbarButton icon={Undo2} onClick={() => editor.chain().focus().undo().run()} tooltip="Undo (Ctrl+Z)" disabled={!editor.can().undo()} />
              <ToolbarButton icon={Redo2} onClick={() => editor.chain().focus().redo().run()} tooltip="Redo (Ctrl+Y)" disabled={!editor.can().redo()} />
            </div>

          {/* Font Family & Size */}
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" onMouseDown={(e) => e.preventDefault()} className="h-9 gap-2 text-[11px] font-bold px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl min-w-[100px]">
                  {currentFontFamily} <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64 max-h-[400px] overflow-y-auto rounded-2xl p-2 shadow-2xl border border-slate-200 dark:border-slate-800 custom-scrollbar">
                <div className="grid grid-cols-1 gap-1">
                  {fontFamilies.map(f => (
                    <DropdownMenuItem 
                      key={f} 
                      onMouseEnter={() => editor.chain().focus().setFontFamily(f).run()} 
                      onMouseLeave={() => editor.chain().focus().setFontFamily(currentFontFamily).run()}
                      onMouseDown={(e) => { e.preventDefault(); setCurrentFontFamily(f); editor.chain().focus().setFontFamily(f).run(); }} 
                      style={{ fontFamily: f }} 
                      className={`rounded-xl cursor-pointer py-2.5 px-3 flex items-center justify-between text-[15px] ${currentFontFamily === f ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 font-bold' : ''}`}
                    >
                      {f}
                      {currentFontFamily === f && <CheckSquare className="h-3.5 w-3.5" />}
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" onMouseDown={(e) => e.preventDefault()} className="h-9 gap-2 text-[11px] font-bold px-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
                  {currentFontSize}px <ChevronDown className="h-3 w-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-24 max-h-[400px] overflow-y-auto rounded-2xl p-1 shadow-2xl custom-scrollbar border border-slate-200 dark:border-slate-800">
                {fontSizes.map(s => (
                  <DropdownMenuItem 
                    key={s} 
                    onMouseEnter={() => editor.chain().focus().setFontSize(`${s}px`).run()}
                    onMouseLeave={() => editor.chain().focus().setFontSize(`${currentFontSize}px`).run()}
                    onMouseDown={(e) => { e.preventDefault(); setCurrentFontSize(s.toString()); editor.chain().focus().setFontSize(`${s}px`).run(); }} 
                    className={`rounded-xl cursor-pointer justify-center font-bold py-2.5 text-xs ${currentFontSize === s.toString() ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' : ''}`}
                  >
                    {s}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

          {/* Text Formatting */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton icon={Bold} onClick={() => editor.chain().focus().toggleBold().run()} tooltip="Bold (Ctrl+B)" active={editor.isActive('bold')} />
            <ToolbarButton icon={Italic} onClick={() => editor.chain().focus().toggleItalic().run()} tooltip="Italic (Ctrl+I)" active={editor.isActive('italic')} />
            <ToolbarButton icon={Underline} onClick={() => editor.chain().focus().toggleUnderline().run()} tooltip="Underline (Ctrl+U)" active={editor.isActive('underline')} />
            <ToolbarButton icon={Strikethrough} onClick={() => editor.chain().focus().toggleStrike().run()} tooltip="Strikethrough" active={editor.isActive('strike')} />
            <ToolbarButton icon={Subscript} onClick={() => editor.chain().focus().toggleSubscript().run()} tooltip="Subscript" active={editor.isActive('subscript')} />
            <ToolbarButton icon={Superscript} onClick={() => editor.chain().focus().toggleSuperscript().run()} tooltip="Superscript" active={editor.isActive('superscript')} />
            <ToolbarButton icon={Code2} onClick={() => editor.chain().focus().toggleCodeBlock().run()} tooltip="Code Block" active={editor.isActive('codeBlock')} />
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

          {/* Text Color */}
          <div className="flex items-center gap-0.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" onMouseDown={(e) => e.preventDefault()} className="h-8 w-8 rounded-lg text-slate-500"><Palette className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent className="p-3 w-64 rounded-2xl shadow-2xl">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Theme Colors</p>
                <div className="grid grid-cols-10 gap-1 mb-3">
                  {['#000000','#434343','#666666','#999999','#b7b7b7','#cccccc','#d9d9d9','#efefef','#f3f3f3','#ffffff',
                    '#980000','#ff0000','#ff9900','#ffff00','#00ff00','#00ffff','#4a86e8','#0000ff','#9900ff','#ff00ff',
                    '#e6b8af','#f4cccc','#fce5cd','#fff2cc','#d9ead3','#d0e0e3','#c9daf8','#cfe2f3','#d9d2e9','#ead1dc',
                    '#dd7e6b','#ea9999','#f9cb9c','#ffe599','#b6d7a8','#a2c4c9','#a4c2f4','#9fc5e8','#b4a7d6','#d5a6bd',
                    '#cc4125','#e06666','#f6b26b','#ffd966','#93c47d','#76a5af','#6d9eeb','#6fa8dc','#8e7cc3','#c27ba0',
                    '#a61c00','#cc0000','#e69138','#f1c232','#6aa84f','#45818e','#3c78d8','#3d85c6','#674ea7','#a64d79'
                  ].map(c => (
                    <div 
                      key={c} 
                      onMouseEnter={() => editor.chain().focus().setColor(c).run()}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setColor(c).run(); }} 
                      className="h-5 w-5 rounded border border-slate-200 cursor-pointer hover:scale-125 transition-transform" 
                      style={{ backgroundColor: c }} 
                    />
                  ))}
                </div>
                <button onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetColor().run(); }} className="text-xs text-rose-500 font-bold">Reset Color</button>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" onMouseDown={(e) => e.preventDefault()} className="h-8 w-8 rounded-lg text-slate-500"><Highlighter className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent className="p-3 w-56 rounded-2xl shadow-2xl">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Highlight</p>
                <div className="grid grid-cols-5 gap-2">
                  {['#ffff00','#00ff00','#00ffff','#ff00ff','#ff6b6b','#ffd43b','#69db7c','#74c0fc','#da77f2','#ffa8a8'].map(c => (
                    <div 
                      key={c} 
                      onMouseEnter={() => editor.chain().focus().setHighlight({ color: c }).run()}
                      onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setHighlight({ color: c }).run(); }} 
                      className="h-8 w-8 rounded border border-slate-200 cursor-pointer hover:brightness-90 transition-all" 
                      style={{ backgroundColor: c }} 
                    />
                  ))}
                  <div onMouseEnter={() => editor.chain().focus().unsetHighlight().run()} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetHighlight().run(); }} className="h-8 w-8 rounded border border-slate-200 cursor-pointer bg-white flex items-center justify-center"><Eraser className="h-3 w-3 text-slate-400" /></div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

          {/* Alignment */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton icon={AlignLeft} onClick={() => editor.chain().focus().setTextAlign('left').run()} tooltip="Left" active={editor.isActive({ textAlign: 'left' })} />
            <ToolbarButton icon={AlignCenter} onClick={() => editor.chain().focus().setTextAlign('center').run()} tooltip="Center" active={editor.isActive({ textAlign: 'center' })} />
            <ToolbarButton icon={AlignRight} onClick={() => editor.chain().focus().setTextAlign('right').run()} tooltip="Right" active={editor.isActive({ textAlign: 'right' })} />
            <ToolbarButton icon={AlignJustify} onClick={() => editor.chain().focus().setTextAlign('justify').run()} tooltip="Justify" active={editor.isActive({ textAlign: 'justify' })} />
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

          {/* Lists */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton icon={List} onClick={() => editor.chain().focus().toggleBulletList().run()} tooltip="Bullet List" active={editor.isActive('bulletList')} />
            <ToolbarButton icon={ListOrdered} onClick={() => editor.chain().focus().toggleOrderedList().run()} tooltip="Numbered List" active={editor.isActive('orderedList')} />
            <ToolbarButton icon={CheckSquare} onClick={() => editor.chain().focus().toggleTaskList().run()} tooltip="Task List" active={editor.isActive('taskList')} />
            <ToolbarButton icon={Indent} onClick={() => editor.commands.indent()} tooltip="Indent (Tab)" />
            <ToolbarButton icon={Outdent} onClick={() => editor.commands.outdent()} tooltip="Outdent (Shift+Tab)" />
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

          {/* Headings */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton icon={Heading1} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} tooltip="Heading 1" active={editor.isActive('heading', { level: 1 })} />
            <ToolbarButton icon={Heading2} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} tooltip="Heading 2" active={editor.isActive('heading', { level: 2 })} />
            <ToolbarButton icon={Heading3} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} tooltip="Heading 3" active={editor.isActive('heading', { level: 3 })} />
            <ToolbarButton icon={Quote} onClick={() => editor.chain().focus().toggleBlockquote().run()} tooltip="Quote" active={editor.isActive('blockquote')} />
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

          {/* Line Height & Paragraph Spacing */}
          <div className="flex items-center gap-0.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" onMouseDown={(e) => e.preventDefault()} className="h-8 w-8 rounded-lg text-slate-500"><MoveVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent className="w-36 rounded-xl p-1">
                {[{v:'1', l:'Single'},{v:'1.15', l:'1.15'},{v:'1.5', l:'1.5 Lines'},{v:'2', l:'Double'},{v:'2.5', l:'2.5 Lines'},{v:'3', l:'Triple'}].map(item => (
                  <DropdownMenuItem key={item.v} onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setLineHeight(item.v).run(); }} className="text-xs font-bold py-2 rounded-lg cursor-pointer">{item.l}</DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetLineHeight().run(); }} className="text-xs font-bold py-2 rounded-lg cursor-pointer text-rose-500">Reset</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />

          {/* Insert */}
          <div className="flex items-center gap-0.5">
            <ToolbarButton icon={Link} onClick={insertLink} tooltip="Link" active={editor.isActive('link')} />
            <ToolbarButton icon={ImageIcon} onClick={insertImage} tooltip="Image" />
            <ToolbarButton icon={Table} onClick={insertTable} tooltip="Table" />
            <ToolbarButton icon={Minus} onClick={() => editor.chain().focus().setHorizontalRule().run()} tooltip="Divider" />
          </div>

          {/* Table Controls */}
          {editor.isActive('table') && (
            <>
              <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />
              <div className="flex items-center gap-0.5 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg px-1 py-0.5">
                <ToolbarButton icon={Plus} onClick={() => editor.chain().focus().addColumnAfter().run()} tooltip="Add Column" />
                <ToolbarButton icon={Plus} onClick={() => editor.chain().focus().addRowAfter().run()} tooltip="Add Row" />
                <ToolbarButton icon={Minus} onClick={() => editor.chain().focus().deleteColumn().run()} tooltip="Remove Column" />
                <ToolbarButton icon={Minus} onClick={() => editor.chain().focus().deleteRow().run()} tooltip="Remove Row" />
                <ToolbarButton icon={Trash2} onClick={() => editor.chain().focus().deleteTable().run()} tooltip="Delete Table" />
              </div>
            </>
          )}

          <div className="h-6 w-px bg-slate-200 dark:bg-slate-800 mx-1" />
          <div className="flex items-center gap-0.5">
            <ToolbarButton icon={Eraser} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} tooltip="Clear Format" />
            <ToolbarButton icon={Trash2} onClick={clearContent} tooltip="Clear All" />
          </div>
          </div>
          </div>
        )}

        {/* ═══ WORKSPACE CONTAINER ═══ */}
        <div className="flex-1 flex overflow-hidden relative">
        
          {/* ═══ PAPER CANVAS ═══ */}
          <div className={cn("flex-1 overflow-y-auto bg-[#adb5bd] dark:bg-[#0f172a] py-4 sm:py-8 custom-scrollbar", uploadedPdfUrl ? "hidden" : "block")}>
            <div className="flex flex-col items-center min-h-max pb-32 px-2 sm:px-4">
              <div className="flex flex-col items-center w-full" style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', transition: 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)' }}>
                <div 
                  className="paper-sheet relative bg-white shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1),_0_8px_10px_-6px_rgba(0,0,0,0.1)] w-full sm:w-[816px]"
                  style={{ minHeight: '1056px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                >
                  <EditorContent editor={editor} className="tiptap-editor-area" style={{ padding: '24px' }} />
                </div>
              </div>
            </div>
          </div>
          
          {/* ═══ PDF READER OVERLAY ═══ */}
          <AnimatePresence>
            {uploadedPdfUrl && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="absolute inset-0 z-30 bg-slate-900 overflow-hidden flex flex-col"
              >
                 <SubTopicContentViewer isEmbedded={true} directPdfUrl={uploadedPdfUrl} onCloseDirect={() => setUploadedPdfUrl(null)} />
              </motion.div>
            )}
          </AnimatePresence>

        </div>

        {/* ═══ FOOTER ═══ */}
        {!uploadedPdfUrl && (
          <footer className="min-h-10 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 lg:px-8 py-1.5 sm:py-0 z-40 shrink-0 select-none">
          <div className="flex items-center gap-3 sm:gap-8">
            <span className="flex items-center gap-2 text-slate-400 text-xs font-bold"><Type className="h-3.5 w-3.5 text-indigo-400" /> {wordCount} <span className="text-slate-300">WORDS</span></span>
            <span className="flex items-center gap-2 text-slate-400 text-xs font-bold"><Hash className="h-3.5 w-3.5 text-indigo-400" /> {charCount} <span className="text-slate-300">CHARS</span></span>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 text-xs text-slate-400 font-bold uppercase tracking-widest">
            <span>UTF-8</span><span>ENG-US</span>
          </div>
        </footer>
        )}

        {/* ═══ FIND & REPLACE ═══ */}
        <AnimatePresence>
          {isFindReplaceOpen && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed top-20 right-3 left-3 sm:left-auto sm:right-12 sm:w-80 bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 rounded-2xl p-4 z-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold uppercase tracking-widest text-indigo-600">Find & Replace</h3>
                <Button variant="ghost" size="icon" onClick={() => setIsFindReplaceOpen(false)} className="h-6 w-6 rounded-full"><X className="h-4 w-4" /></Button>
              </div>
              <div className="space-y-3">
                <Input placeholder="Find..." value={findText} onChange={(e) => setFindText(e.target.value)} className="h-8 text-xs rounded-lg" />
                <Input placeholder="Replace with..." value={replaceText} onChange={(e) => setReplaceText(e.target.value)} className="h-8 text-xs rounded-lg" />
                <Button onClick={findAndReplace} className="w-full h-8 rounded-lg text-xs bg-indigo-600 hover:bg-indigo-700 text-white">Replace All</Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ═══ STYLES ═══ */}
        <style>{`
          @media (min-width: 640px) {
            .tiptap-editor-area { padding: 64px !important; }
          }
          @media (min-width: 1024px) {
            .tiptap-editor-area { padding: 96px !important; }
          }
          .tiptap-editor-area .tiptap { outline: none; min-height: 11in; line-height: 1.6; font-size: 16px; color: #1a1a1a; word-break: break-word; }
          .tiptap-editor-area .tiptap p.is-editor-empty:first-child::before { content: attr(data-placeholder); float: left; color: #94a3b8; font-style: italic; pointer-events: none; height: 0; }
          .tiptap-editor-area .tiptap h1 { font-size: 32px; line-height: 1.3; border-bottom: 2px solid #eee; margin-bottom: 16px; padding-bottom: 8px; color: #000; font-weight: 800; }
          .tiptap-editor-area .tiptap h2 { font-size: 24px; line-height: 1.3; margin-top: 24px; margin-bottom: 12px; color: #333; font-weight: 700; }
          .tiptap-editor-area .tiptap h3 { font-size: 20px; line-height: 1.4; margin-top: 20px; margin-bottom: 8px; color: #444; font-weight: 600; }
          .tiptap-editor-area .tiptap p { margin: 4px 0; min-height: 24px; }
          .tiptap-editor-area .tiptap ul { list-style-type: disc; padding-left: 24px; margin: 8px 0; }
          .tiptap-editor-area .tiptap ol { list-style-type: decimal; padding-left: 24px; margin: 8px 0; }
          .tiptap-editor-area .tiptap li { margin: 4px 0; }
          .tiptap-editor-area .tiptap li p { margin: 0; }
          .tiptap-editor-area .tiptap ul[data-type="taskList"] { list-style: none; padding-left: 8px; }
          .tiptap-editor-area .tiptap ul[data-type="taskList"] li { display: flex; align-items: flex-start; gap: 8px; }
          .tiptap-editor-area .tiptap ul[data-type="taskList"] li label input[type="checkbox"] { accent-color: #4f46e5; width: 16px; height: 16px; cursor: pointer; margin-top: 4px; }
          .tiptap-editor-area .tiptap blockquote { border-left: 4px solid #6366f1; padding: 8px 16px; margin: 12px 0; background: #f8fafc; border-radius: 0 6px 6px 0; color: #475569; font-style: italic; }
          .tiptap-editor-area .tiptap hr { border: none; border-top: 2px solid #e2e8f0; margin: 24px 0; }
          .tiptap-editor-area .tiptap a, .tiptap-editor-area .tiptap .editor-link { color: #4f46e5; text-decoration: underline; cursor: pointer; }
          .tiptap-editor-area .tiptap pre { background: #1e293b; color: #e2e8f0; padding: 16px 20px; border-radius: 8px; margin: 12px 0; font-family: 'Courier New', monospace; font-size: 14px; overflow-x: auto; line-height: 1.5; }
          .tiptap-editor-area .tiptap pre code { color: inherit; background: none; padding: 0; }
          .tiptap-editor-area .tiptap code { background: #f1f5f9; color: #e11d48; padding: 2px 6px; border-radius: 4px; font-size: 0.9em; font-family: 'Courier New', monospace; }
          .tiptap-editor-area .tiptap table { border-collapse: collapse; table-layout: fixed; width: 100%; margin: 12px 0; }
          .tiptap-editor-area .tiptap table td, .tiptap-editor-area .tiptap table th { min-width: 50px; border: 1px solid #cbd5e1; padding: 8px 12px; vertical-align: top; position: relative; }
          .tiptap-editor-area .tiptap table th { font-weight: 700; background-color: #f8fafc; }
          .tiptap-editor-area .tiptap table .selectedCell:after { z-index: 2; position: absolute; content: ""; left: 0; right: 0; top: 0; bottom: 0; background: rgba(79,70,229,0.08); pointer-events: none; }
          .tiptap-editor-area .tiptap .tableWrapper { overflow-x: auto; }
          .tiptap-editor-area .tiptap table .column-resize-handle { position: absolute; right: -2px; top: 0; bottom: -2px; width: 4px; background-color: #6366f1; pointer-events: none; }
          .tiptap-editor-area .tiptap sub { font-size: 0.75em; }
          .tiptap-editor-area .tiptap sup { font-size: 0.75em; }
          /* Force selection highlight to be clearly visible */
          .tiptap-editor-area .tiptap ::selection { background-color: #b3d4fc !important; color: #000 !important; }
          .tiptap-editor-area .tiptap::-moz-selection { background-color: #b3d4fc !important; color: #000 !important; }
          .tiptap-editor-area .tiptap *::selection { background-color: #b3d4fc !important; color: #000 !important; }
          .tiptap-editor-area .tiptap *::-moz-selection { background-color: #b3d4fc !important; color: #000 !important; }
          /* Force highlighted text (using mark) to always be dark and clearly visible */
          .tiptap-editor-area .tiptap mark { color: #1a1a1a !important; padding: 2px 1px; border-radius: 2px; }
          .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
          .no-scrollbar::-webkit-scrollbar { display: none; }
          @media print {
            body { background: white !important; }
            header, footer, .sticky, button, [class*="backdrop"] { display: none !important; }
            html, body, #root, .w-full, .flex-1, .custom-scrollbar, .overflow-y-auto { height: auto !important; min-height: auto !important; overflow: visible !important; background: white !important; padding: 0 !important; margin: 0 !important; box-shadow: none !important; }
            .paper-sheet { width: 100% !important; margin: 0 !important; border: none !important; box-shadow: none !important; }
          }
        `}</style>
      </div>
    </TooltipProvider>
  );
};

export default CourseOutlineWriter;
